<template>
  <ErrorBoundary>
    <div class="text-sm font-medium flex-1">
      <div class="grow">
        <div>
          <label>
            {{ t('product.show.checkout.selectDate') }}
          </label>
        </div>
        <!--   BEWARE: adding the allowedDates props to the datePickerProps obj seems to
             break reactivity and thus break the component   -->

        <Datepicker
          ref="datePickerRef"
          v-model="internalSelectedDate"
          v-bind="datePickerProps"
          :allowed-dates="allowedDays"
          :markers="orderItemId ? undefined : calendarCellMarkers"
          @open="onOpenCalendar"
          @closed="onCloseCalendar"
          @update-month-year="handleChangeMonthYear"
          @update:model-value="handleUpdateModelValue"
          @overlay-toggle="handleOverlayToggle"
        >
          <template v-if="!orderItemId" #action-row="{ closePicker }">
            <EventBuyBoxDatePickerNoAvailableDates
              v-if="shouldDisplayNoAvailableEvents"
              :ticket-quantity="selectedTicketsQuantity"
              @click:request-date="closePicker"
            />
            <EventBuyBoxDatePickerDateRequestMenu
              v-else-if="shouldDisplayDateRequestMenu"
              @click:request-date="
                openDateRequestMenuWithDate(lastSelectedDateRequestDay)
              "
            />
            <EventBuyBoxDatePickerCaptions v-else :disable="['soldOut']" />
          </template>
          <template #input-icon>
            <CalendarIcon class="ml-2 fill-text-700" />
          </template>
          <template #clear-icon>
            <ChevronRightIcon class="mr-4 fill-text-600 rotate-90" />
          </template>
          <template #day="{ day }">
            <div class="mt-1 flex-1 flex items-center justify-center">
              {{ day }}
            </div>
          </template>
          <template v-if="!orderItemId" #marker="{ marker }">
            <div class="block w-full flex overflow-hidden mt-1.5 gap-0.5">
              <div
                v-for="color in marker.color"
                :key="color"
                :class="{
                  'w-full h-[4px] rounded': marker.type === 'line',
                  'w-[4px] h-[4px] rounded mx-auto my-0': marker.type === 'dot',
                }"
                :style="`background-color:${color}`"
              ></div>
            </div>
          </template>
        </Datepicker>
      </div>
    </div>
  </ErrorBoundary>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type DatepickerComponent from '@vuepic/vue-datepicker'
import type {
  EventCalendarDay,
  EventCalendarDayMap,
  EventDescription, EventDate 
} from '~/composables/types/event'
import CalendarIcon from '~/assets/icons/calendar.svg?component'
import ChevronRightIcon from '~/assets/icons/chevron-right.svg?component'
import EventBuyBoxDatePickerNoAvailableDates from '~/components/event/buybox/EventBuyBoxDatePickerNoAvailableDates.vue'
import EventBuyBoxDatePickerDateRequestMenu from '~/components/event/buybox/EventBuyBoxDatePickerDateRequestMenu.vue'
import EventBuyBoxDatePickerCaptions from '~/components/event/buybox/EventBuyBoxDatePickerCaptions.vue'
import { eventDateGetters } from '~/utils/eventDateGetters'
import { useEventBuyBoxDatePickerSession } from '~/composables/useEventBuyBoxDatePickerSession'
import { usePreferredDateIdFromQuery } from '~/composables/usePreferredDateIdFromQuery'
import { useIsSubdomain } from '~/composables/useSubdomain'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'

dayjs.extend(utc)
dayjs.extend(timezone)

// Reserves the closed-input footprint while the async datepicker chunk loads,
// so EventBuyBoxTimeSlots and the CTA below it don't shift (no CLS).
const DatepickerInputPlaceholder = defineComponent({
  name: 'DatepickerInputPlaceholder',
  render: () =>
    h('div', { class: 'mt-2 h-[42px] rounded-lg', 'aria-hidden': 'true' }),
})

// Code-split @vuepic/vue-datepicker (JS + CSS) out of the eager event-page bundle.
// The wrapper itself still mounts normally, so first-date auto-selection, pricing
// and the on-dates-loaded emit keep running on the same schedule.
const Datepicker = defineAsyncComponent({
  loader: async () => {
    await import('@vuepic/vue-datepicker/dist/main.css')
    return import('@vuepic/vue-datepicker')
  },
  loadingComponent: DatepickerInputPlaceholder,
  delay: 0,
})

const {
  enableDateRequestCalendarCells = false,
  selectedTicketsQuantity,
  dateRequestTicketQuantity,
  orderItemId,
  firstAvailableDate,
  firstAvailableMonth,
} = defineProps<{
  selectedTicketsQuantity: number
  /** Passed to the date-request sidebar only after the user changes buy-box party size. */
  dateRequestTicketQuantity?: number
  orderItemId?: string
  firstAvailableDate?: string
  firstAvailableMonth?: EventCalendarDayMap
  enableDateRequestCalendarCells?: boolean
}>()

const emit = defineEmits([
  'open-date-picker',
  'on-dates-loaded',
  'close-date-picker',
])

const datePickerRef = ref<InstanceType<typeof DatepickerComponent> | undefined>(
  undefined
)
const selectedDay = defineModel<EventCalendarDay | undefined>(undefined)
const internalSelectedDate = ref<string | undefined>(undefined)
const currentYearMonthOnCalendar = ref<string | undefined>(undefined)
const shouldDisplayDateRequestMenu = ref<boolean>(false)
const lastSelectedDateRequestDay = ref<string | undefined>(undefined)

const eventData = inject<EventDescription>('eventData')!
const eventId = computed(() => eventGetters.getId(eventData))
const preferredDateId = usePreferredDateIdFromQuery()
const isSubdomain = useIsSubdomain()
const config = useRuntimeConfig()
const { toggleDateRequestSidebar } = useUiState()
const route = useRoute()
const { saveFormDateFromCalendar } = useDateRequestFormStorage()

const { onResponse, fetchedMonths } = useFetchedMonths(eventId.value)
const { t } = useI18n()
const { isAnyOverlayOpen, onOverlayToggle } = useDatePickerOverlay()

const openDatePicker = () => {
  if (datePickerRef.value) datePickerRef.value.openMenu()
}

// Initialize with the first available month from props
const calendarDayMap = ref<EventCalendarDayMap>(firstAvailableMonth || {})

const fetchCalendar = async (monthToFetch: string) => {
  if (!dayjs(monthToFetch).isValid()) return

  const { data } = await useKftApiFetch<EventCalendarDayMap>(
    `v1/store/events/${eventId.value}/calendar`,
    {
      key: `calendar-${eventId.value}-${monthToFetch}`,
      query: {
        month: monthToFetch,
        ...(orderItemId && {
          with_rebooking: orderItemId,
        }),
      },
      server: false,
      onResponse,
    }
  )

  calendarDayMap.value = data.value || {}
}

const mergedCalendarDayMap = useMergedCalendarDayMap(
  eventId.value,
  calendarDayMap
)

const allowedDays = computed(() => {
  const calendarDays = eventDateGetters.getAllowedDays(
    mergedCalendarDayMap.value
  )
  const dateRequestDays = getDateRequestAvailableDays(eventData)

  if (!enableDateRequestCalendarCells) {
    return calendarDays
  }

  return [...calendarDays, ...dateRequestDays]
})

const dateRequestEnabled = computed(() =>
  eventGetters.isDateRequestActive(eventData)
)

const nextAvailableDate = computed(
  () => firstAvailableDate || allowedDays.value?.[0] || Date.now()
)

const nextAvailableDay = computed(() =>
  dayjs(new Date(nextAvailableDate.value)).format('YYYY-MM-DD')
)

const nextAvailableDateMonth = computed(() =>
  dayjs(new Date(nextAvailableDate.value)).format('YYYY-MM')
)

const maxDate = computed(() => eventGetters.getMaxDate(eventData))

/** Matches :allowed-dates ∩ minDate/maxDate so markers are not shown on picker-disabled cells. */
const markerAllowedDays = computed(() => {
  if (orderItemId) {
    return allowedDays.value
  }

  const minPickerDay = dayjs(
    dayjs(nextAvailableDate.value).format('YYYY-MM-DD'),
    'YYYY-MM-DD'
  )
  const maxPickerDay = dayjs(
    dayjs.tz(maxDate.value, 'UTC').add(2, 'month').format('YYYY-MM-DD'),
    'YYYY-MM-DD'
  )

  return allowedDays.value.filter((dateStr) => {
    const d = dayjs(dateStr, 'YYYY-MM-DD')
    return (
      (d.isAfter(minPickerDay, 'day') || d.isSame(minPickerDay, 'day')) &&
      (d.isBefore(maxPickerDay, 'day') || d.isSame(maxPickerDay, 'day'))
    )
  })
})

const calendarCellMarkers = computed(() => {
  const markers = useCalendarCellMarkers(
    mergedCalendarDayMap,
    computed(() => selectedTicketsQuantity),
    eventData,
    markerAllowedDays,
    t
  )
  // Type assertion: @vuepic/vue-datepicker supports string arrays for colors despite type definition
  return markers.value as any
})

// Check if selected date is a date request day (not in calendar)
const isDateRequestDay = computed(() => {
  if (!internalSelectedDate.value) return false
  return !mergedCalendarDayMap.value[internalSelectedDate.value]
})

const calendarIsPastMaxMonth = computed(() =>
  dayjs
    .tz(currentYearMonthOnCalendar.value)
    .isAfter(dayjs.tz(maxDate.value, 'UTC'))
)

const shouldDisplayNoAvailableEvents = computed(
  () => calendarIsPastMaxMonth.value && !isAnyOverlayOpen.value && !orderItemId
)
const shouldDisplayCalendar = computed(() => {
  return (
    shouldDisplayNoAvailableEvents.value || shouldDisplayDateRequestMenu.value
  )
})

const datePickerProps = computed(() => ({
  config: { shadowDom: true, keepActionRow: true, closeOnAutoApply: false },
  autoApply: !shouldDisplayNoAvailableEvents.value,
  modelType: 'yyyy-MM-dd',
  format: (date: string) => {
    if (!date) return ''
    return dayjs(date).locale('de').format('ddd DD.MM.YYYY')
  },
  enableTimePicker: false,
  ...(!orderItemId && {
    minDate: new Date(nextAvailableDate.value),
  }),
  locale: 'de',
  shortcuts: false,
  ui: {
    input: '!text-sm !rounded-lg !py-2',
    menu: 'event-buy-box-calendar',
    calendarCell: 'event-buy-box-calendar-cell',
    // Hides calendar if past event's max_date
    calendar: shouldDisplayCalendar.value ? 'hidden' : '',
  },
  monthChangeOnScroll: false,
  position: 'left',
  autoPosition: 'bottom',
  noInput: true,
  // Max date prevents user from selecting next month
  // Max date is not applied to rebooking requests
  ...(!orderItemId && {
    maxDate: dayjs.tz(maxDate.value, 'UTC').add(2, 'month').toDate(),
  }),
  preventMinMaxNavigation: true,
  ...(!orderItemId && {
    yearRange: [dayjs().year(), dayjs.tz(maxDate.value, 'UTC').year()],
  }),
  class: 'mt-2',
}))

const shouldFetchMonth = (month: string) => {
  const isNextMonthAfterNextAvailableDate = dayjs(month, 'YYYY-MM').isAfter(
    dayjs(nextAvailableDateMonth.value, 'YYYY-MM')
  )

  const isNextMonthAfterMaxMonth = dayjs(month, 'YYYY-MM').isAfter(
    dayjs(maxDate.value, 'YYYY-MM')
  )

  const isNextMonthFetched = fetchedMonths.value[month]

  return (
    !isNextMonthFetched &&
    isNextMonthAfterNextAvailableDate &&
    (!isNextMonthAfterMaxMonth || orderItemId)
  )
}

const fetchMonth = async (month: string) => {
  if (shouldFetchMonth(month)) {
    await fetchCalendar(month)
  }
}

const setCurrentYearMonth = () => {
  const today = dayjs()

  datePickerRef.value?.setMonthYear({
    month: today.month(),
    year: today.year(),
  })
}

const handleChangeMonthYear = async ({
  year,
  month,
}: {
  year: number
  month: number
}) => {
  const newMonth = dayjs().set('month', month).set('year', year)

  const today = dayjs()

  const isMonthYearIsBeforeTodaysMonth = newMonth.isBefore(today)

  if (isMonthYearIsBeforeTodaysMonth) {
    setCurrentYearMonth()
    return
  }

  if (!newMonth.isValid()) return
  currentYearMonthOnCalendar.value = newMonth.format('YYYY-MM')

  await fetchMonth(newMonth.format('YYYY-MM'))
  await fetchMonth(newMonth.add(1, 'month').format('YYYY-MM'))
}

const selectFirstAvailableDay = () => {
  const firstAvailableDay = allowedDays.value?.[0] || nextAvailableDay.value

  if (!firstAvailableDay) return
  internalSelectedDate.value = firstAvailableDay

  return mergedCalendarDayMap.value[firstAvailableDay]
}

const selectPreferredDateFromQuery = async (): Promise<boolean> => {
  const dateId = preferredDateId.value
  if (!dateId) return false

  try {
    const response = await $fetch<KftApiDefaultResponse<EventDate[]>>(
      `v1/store/events/${eventId.value}/dates`,
      {
        baseURL: config.public.apiBaseURL,
        query: {
          search: `id:${dateId};${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}`,
          include: 'children',
        },
      }
    )

    const preferredDate = response?.data?.[0]
    const startTime = preferredDate
      ? eventDateGetters.getStartTime(preferredDate)
      : ''
    if (!startTime) return false

    const day = dayjs(startTime).format('YYYY-MM-DD')
    const month = dayjs(startTime).format('YYYY-MM')
    await fetchMonth(month)
    // The calendar endpoint buckets a month's 1st day only under the
    // previous month's response (grid spillover), so day-1 dates are
    // missing from their own month's fetch — cover that gap too.
    await fetchMonth(
      dayjs(month, 'YYYY-MM').subtract(1, 'month').format('YYYY-MM')
    )
    internalSelectedDate.value = day
    return true
  } catch (error) {
    console.error('Failed to load preferred date from query:', error)
    return false
  }
}

const onOpenCalendar = () => {
  currentYearMonthOnCalendar.value = dayjs(internalSelectedDate.value).format(
    'YYYY-MM'
  )

  emit('open-date-picker')
}

const onCloseCalendar = async () => {
  shouldDisplayDateRequestMenu.value = false
  // -- start of workaround
  // reset the selected date to undefined and then set it back to the previous value
  // just because without this, date picker will display an empty value when closing the calendar
  // if the date request menu was open
  const tempSelectedDate = internalSelectedDate.value
  internalSelectedDate.value = undefined
  await nextTick()
  internalSelectedDate.value = tempSelectedDate
  // -- end of workaround
  emit('close-date-picker')
}

const setupMonths = async () => {
  // Only fetch the next month since the first month is already provided via props
  const nextMonth = dayjs(nextAvailableDateMonth.value)
    .add(1, 'month')
    .format('YYYY-MM')
  await fetchMonth(nextMonth)
}

const { updateDateFromSession } = useEventBuyBoxDatePickerSession(
  internalSelectedDate,
  mergedCalendarDayMap,
  fetchMonth
)

// Close date request menu when overlay is toggled (clicking on month or year)
const handleOverlayToggle = (overlay: { overlay: string; open: boolean }) => {
  onOverlayToggle(overlay)
  shouldDisplayDateRequestMenu.value = false
}

const openDateRequestMenuWithDate = (date: string | undefined) => {
  // if (!date) return
  saveFormDateFromCalendar(
    dayjs(date, 'YYYY-MM-DD').hour(16).minute(0).format('YYYY-MM-DDTHH:mm')
  )
  toggleDateRequestSidebar(
    route.name?.toString() || '',
    'buybox_calendar_date_picker',
    undefined,
    dateRequestTicketQuantity
  )
  datePickerRef.value?.closeMenu()
}

const handleUpdateModelValue = () => {
  // close menu if not a date request day
  // doing it manually cause
  if (!isDateRequestDay.value) {
    datePickerRef.value?.closeMenu()
  }
}

watch(
  () => internalSelectedDate.value,
  (newDate, oldDate) => {
    if (newDate) {
      selectedDay.value = mergedCalendarDayMap.value[newDate]
      if (isDateRequestDay.value && dateRequestEnabled.value) {
        // restore the old date
        internalSelectedDate.value = oldDate
        lastSelectedDateRequestDay.value = newDate
        shouldDisplayDateRequestMenu.value = true
      }
    }
  }
)

watch(
  () => currentYearMonthOnCalendar.value,
  () => {
    shouldDisplayDateRequestMenu.value = false
  }
)

// Initialize on component mount
onMounted(async () => {
  await setupMonths()
  emit('on-dates-loaded')

  const selectedFromQuery = await selectPreferredDateFromQuery()
  if (!selectedFromQuery) {
    selectFirstAvailableDay()
  }
  await updateDateFromSession()
})

defineExpose({
  openDatePicker,
  isDateRequestDay,
})
</script>

<style lang="postcss" scoped>
@import '~/assets/css/components/event/buybox/EventBuyBoxDatePicker.css';
</style>
